import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

type Tint = "accent" | "success" | "warning" | "danger" | "neutral";

const tintClass: Record<Tint, string> = {
  accent:
    "border-[color:var(--color-accent)] text-[color:var(--color-accent)] bg-transparent",
  success:
    "border-[color:var(--color-success)] text-[color:var(--color-success)] bg-transparent",
  warning:
    "border-[color:var(--color-warning)] text-[color:var(--color-warning)] bg-transparent",
  danger:
    "border-[color:var(--color-danger)] text-[color:var(--color-danger)] bg-transparent",
  neutral:
    "border-[color:var(--color-border)] text-[color:var(--color-text-md)] bg-transparent",
};

const statusTints: Record<string, Tint> = {
  // Customer lifecycle
  lead: "accent",
  prospect: "accent",
  customer: "success",
  former_customer: "neutral",
  inactive: "neutral",
  // Proposal
  draft: "neutral",
  sent: "accent",
  viewed: "neutral",
  approved: "success",
  rejected: "danger",
  expired: "neutral",
  // Job (ops.job_stage)
  new: "warning",
  scheduled: "accent",
  in_progress: "neutral",
  completed: "success",
  canceled: "danger",
  // Legacy job statuses retained for safety while older clients/data exist;
  // remove once no caller emits these values.
  pending: "warning",
  cancelled: "danger",
  // Commission status (finance.commissions — ADV-1147). pending/paid/cancelled
  // are already covered above; payable signals "approved, awaiting payout".
  payable: "accent",
  // Appointment (sales.appointments.status — ADV-1143)
  no_show: "danger",
  rescheduled: "warning",
  // Invoice
  paid: "success",
  partial: "warning",
  overdue: "danger",
  refunded: "neutral",
  // Opportunity stages (core.opportunity_stage)
  appointment: "warning",
  proposal: "accent",
  closed_won: "success",
  closed_lost: "danger",
  // Approval
  auto_approved: "success",
  // Team roles
  admin: "success",
  manager: "accent",
  sales_rep: "success",
  production: "warning",
  finance: "neutral",
  // Import job
  validating: "accent",
  importing: "accent",
  staged: "warning",
  normalizing: "warning",
  awaiting_review: "warning",
  partially_approved: "success",
  discarded: "neutral",
  // User
  invited: "warning",
  // Integration connections
  disconnected: "neutral",
  error: "danger",
  reauth_required: "warning",
  // Promotion
  active: "success",
  percentage: "accent",
  fixed: "warning",
};

const labelOverrides: Record<string, string> = {
  closed_won: "Closed Won",
  closed_lost: "Closed Lost",
  proposal: "Proposed",
  completed: "Complete",
};

function formatLabel(status: string): string {
  if (labelOverrides[status]) return labelOverrides[status];
  return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function StatusBadge({
  status,
  label,
  className,
}: {
  status: string | null | undefined;
  label?: string;
  className?: string;
}) {
  if (!status) return null;
  const tint = statusTints[status] ?? "neutral";
  return (
    <Badge
      variant="outline"
      className={cn("rounded-sm typo-label", tintClass[tint], className)}
    >
      {label ?? formatLabel(status)}
    </Badge>
  );
}
